import { useEffect, useMemo, useRef, useState } from "react";
import { sortBy } from "lodash";
import FieldLabel from "@shared/v2/FieldLabel";
import FieldError from "@shared/v2/FieldError";
import PropTypes from "prop-types";
import Dropdown from "@shared/v2/Dropdown/Dropdown";
import Toggle from "@shared/v2/Toggle";
import DisableConnectServiceDropdown from "../DisableConnectServiceDropdown";
import useDropdownSearch from "../../SideLeft/People/AddModal/hooks";

const useTransactionClientSearch = ({ uuid }) => {
  const { noOptionsMessage, availablePeopleDropdown, setSearch, isLoading, error } = useDropdownSearch({
    title: "Client",
    uuid,
  });
  return {
    options: availablePeopleDropdown,
    setSearch,
    isLoading,
    error,
    noOptionsMessage,
  };
};

const NoAvailableClientDropdowns = ({
  onSelect,
  onValidityChange,
  onRoleChange,
  error,
  clientRoles,
  defaultRole,
  options,
  setSearch,
  isLoading,
  searchError,
  noOptionsMessage,
}) => {
  const rolesDropdown = useMemo(
    () =>
      sortBy(
        (clientRoles || []).map((role) => ({
          value: role.id,
          label: role.title,
        })),
        "label",
      ),
    [clientRoles],
  );

  const [selectedRole, setSelectedRole] = useState(defaultRole || null);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    const isValid = Boolean(selectedRole && selectedClient);
    if (isValid) {
      onSelect({ role: selectedRole, client: selectedClient });
    }
    if (onValidityChange) {
      onValidityChange(isValid);
    }
  }, [selectedRole, selectedClient]);

  return (
    <div className="tw-flex tw-flex-col tw-gap-[4px]">
      <FieldLabel label="Which client role should they contact?" isRequired />
      <div className="tw-flex tw-gap-[4px]">
        <Dropdown
          className="tw-w-[148px]"
          containerClassName="tw-w-[148px]"
          placeholder="Select role"
          options={rolesDropdown}
          value={selectedRole}
          onChange={(role) => {
            setSelectedRole(role);
            setSelectedClient(null);
            if (onRoleChange) onRoleChange(role);
          }}
          menuShouldComeToFront
        />
        <Dropdown
          className="tw-flex-1"
          containerClassName="tw-flex-1 tw-overflow-hidden"
          placeholder="Search for a contact to assign to this role"
          options={options}
          value={selectedClient}
          onChange={setSelectedClient}
          onInputChange={setSearch}
          isSearchable
          isLoading={isLoading}
          noOptionsMessage={noOptionsMessage}
          menuShouldComeToFront
          error={error || searchError}
        />
      </div>
      <span className="tw-text-12d tw-text-gray-50">This contact will be added to the transaction</span>
    </div>
  );
};

const ConnectServicesClientSelector = ({
  clientOptions,
  selectedClient,
  onClientChange,
  error,
  showToggle,
  showSendTodayText,
  showSendFutureText,
  disableReason,
  onDisableReasonChange,
  onValidityChange,
  uuid,
  clientRoles,
  useSearchClient,
  isCloseDateInPast,
}) => {
  const [isEnabled, setIsEnabled] = useState(true);
  const [noClientValid, setNoClientValid] = useState(false);
  const [selectedNullRole, setSelectedNullRole] = useState(null);
  const hasAutoSelected = useRef(false);

  const { options, setSearch, isLoading, error: searchError, noOptionsMessage } = useSearchClient({ uuid });

  useEffect(() => {
    if (!onValidityChange) return;

    if (isCloseDateInPast) {
      onValidityChange(true);
      return;
    }

    if (showToggle && !isEnabled) {
      onValidityChange(Boolean(disableReason?.value));
    } else if (clientOptions.length === 0 || selectedNullRole) {
      onValidityChange(noClientValid);
    } else {
      onValidityChange(Boolean(selectedClient?.value));
    }
  }, [
    isCloseDateInPast,
    isEnabled,
    showToggle,
    disableReason,
    selectedClient,
    clientOptions,
    noClientValid,
    selectedNullRole,
    onValidityChange,
  ]);

  const roleBasedOptions = useMemo(() => {
    if (!clientRoles || clientRoles.length === 0) return clientOptions;

    return sortBy(
      clientRoles.map((role) => {
        const matchedClient = clientOptions.find((c) => c.roleId === role.id);
        if (matchedClient) {
          return {
            label: `${role.title} (${matchedClient.name} ${matchedClient.email})`,
            value: matchedClient.value,
          };
        }
        return {
          label: role.title,
          value: null,
          roleId: role.id,
        };
      }),
      "label",
    );
  }, [clientRoles, clientOptions]);

  useEffect(() => {
    if (hasAutoSelected.current) return;
    if (clientOptions.length > 0 && !selectedClient && roleBasedOptions.length > 0) {
      const firstClient = clientOptions[0];
      const matchedOption = roleBasedOptions.find((o) => o.value === firstClient.value);
      if (matchedOption) {
        hasAutoSelected.current = true;
        onClientChange(matchedOption);
      }
    }
  }, [roleBasedOptions]);

  const handleClientChange = (option) => {
    if (option && option.value === null) {
      setSelectedNullRole({ value: option.roleId, label: option.label });
      onClientChange(null);
    } else {
      setSelectedNullRole(null);
      onClientChange(option);
    }
  };

  const handleNoClientRoleChange = (role) => {
    const matchedClient = clientOptions.find((c) => c.roleId === role?.value);
    if (matchedClient) {
      const matchedOption = roleBasedOptions.find((o) => o.value === matchedClient.value);
      setSelectedNullRole(null);
      onClientChange(matchedOption || null);
    } else {
      setSelectedNullRole(role);
    }
  };

  const DropdownClient =
    clientOptions.length === 0 || selectedNullRole ? (
      <NoAvailableClientDropdowns
        onSelect={onClientChange}
        onValidityChange={setNoClientValid}
        onRoleChange={handleNoClientRoleChange}
        error={error}
        clientRoles={clientRoles}
        defaultRole={selectedNullRole}
        options={options}
        setSearch={setSearch}
        isLoading={isLoading}
        searchError={searchError}
        noOptionsMessage={noOptionsMessage}
      />
    ) : (
      <Dropdown
        label="Which client role should they contact?"
        isRequired
        placeholder="Select"
        options={roleBasedOptions}
        value={selectedClient}
        onChange={handleClientChange}
        error={error}
        menuShouldComeToFront
      />
    );

  if (isCloseDateInPast) {
    return (
      <div className="tw-border tw-border-solid tw-border-gray-10 tw-rounded-[8px] tw-p-[16px] tw-flex tw-flex-col tw-gap-[16px]">
        <div className="tw-flex tw-items-center tw-justify-between">
          <div className="tw-text-14d tw-font-semibold tw-text-gray-75">
            Connect Services: Free Moving Concierge
          </div>
          <Toggle checked={false} onChange={() => {}} disabled />
        </div>
        <p className="tw-text-14d tw-text-gray-50">
          Connect Services referral is not available for transactions if the close date is before today.
        </p>
      </div>
    );
  }

  return (
    <div className="tw-border tw-border-solid tw-border-gray-10 tw-rounded-[8px] tw-p-[16px] tw-flex tw-flex-col tw-gap-[16px]">
      <div className="tw-flex tw-flex-col tw-gap-[4px]">
        <div className="tw-flex tw-items-center tw-justify-between">
          <div className="tw-text-14d tw-font-semibold tw-text-gray-75">
            Connect Services: Free Moving Concierge
          </div>
          {showToggle && (
            <Toggle
              checked={isEnabled}
              onChange={() => {
                if (isEnabled) {
                  onClientChange(null);
                } else {
                  onDisableReasonChange(null);
                }
                setIsEnabled(!isEnabled);
              }}
            />
          )}
        </div>
        {isEnabled ? (
          <div className="tw-text-12d tw-text-gray-50 tw-flex tw-flex-col tw-gap-[16px] tw-pr-[40px]">
            <div>
              Automatically refer this client to a moving specialist to get their home services connected free
              of charge.{" "}
              {showSendFutureText && (
                <span>
                  This contact’s info{" "}
                  <b>will be sent to Connect Services 21 days before the close date.</b>{" "}
                </span>
              )}
            </div>
            {showSendTodayText && (
              <div>
                Looks like the close date is coming up soon! For the best customer experience,{" "}
                <span className="tw-font-semibold tw-text-[#E5941B]">
                  we&apos;ll automatically send this referral today
                </span>
                .
              </div>
            )}
          </div>
        ) : null}
      </div>
      {showToggle && !isEnabled ? (
        <div>
          <DisableConnectServiceDropdown value={disableReason} onChange={onDisableReasonChange} />
          <FieldError className="tw-mt-4px" error="Please record a reason to share with your team." />
        </div>
      ) : (
        DropdownClient
      )}
    </div>
  );
};

ConnectServicesClientSelector.propTypes = {
  clientOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      roleId: PropTypes.number,
      name: PropTypes.string,
      email: PropTypes.string,
    }),
  ),
  selectedClient: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onClientChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  showToggle: PropTypes.bool,
  showSendTodayText: PropTypes.bool,
  showSendFutureText: PropTypes.bool,
  disableReason: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  onDisableReasonChange: PropTypes.func,
  onValidityChange: PropTypes.func,
  uuid: PropTypes.string,
  clientRoles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
    }),
  ),
  useSearchClient: PropTypes.func,
  isCloseDateInPast: PropTypes.bool,
};

ConnectServicesClientSelector.defaultProps = {
  clientOptions: [],
  selectedClient: null,
  error: null,
  showToggle: false,
  showSendTodayText: false,
  showSendFutureText: false,
  disableReason: null,
  onDisableReasonChange: () => {},
  onValidityChange: null,
  uuid: null,
  clientRoles: null,
  useSearchClient: useTransactionClientSearch,
  isCloseDateInPast: false,
};

NoAvailableClientDropdowns.propTypes = {
  onSelect: PropTypes.func.isRequired,
  onValidityChange: PropTypes.func,
  onRoleChange: PropTypes.func,
  error: PropTypes.string,
  clientRoles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
    }),
  ),
  defaultRole: PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
  }),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    }),
  ),
  setSearch: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  searchError: PropTypes.string,
  noOptionsMessage: PropTypes.string,
};

NoAvailableClientDropdowns.defaultProps = {
  onValidityChange: null,
  onRoleChange: null,
  error: null,
  clientRoles: null,
  defaultRole: null,
  options: [],
  isLoading: false,
  searchError: null,
  noOptionsMessage: "",
};

export default ConnectServicesClientSelector;
